<template>
  <div class="certificate-container">
    <!-- Watermark component (shown when showWatermark is true) -->
    <Watermark v-if="certificateData.showWatermark" />

    <!-- Header -->
    <header class="certificate-header">
      <div class="header-bar">
        <a
          href="https://www.oxvalue.ai/"
          class="header-logo"
          title="OxValue.AI"
          target="_blank"
          data-image-width="575"
          data-image-height="136"
        >
          <img src="/images/logo1.png" class="logo-image" alt="OxValue.AI Logo" />
        </a>
        <button id="downloadButton" @click="downloadPDF">Download PDF</button>
      </div>
    </header>

    <!-- Certificate Content -->
    <div class="certificate-content-wrapper" ref="wrapperRef">
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-state">
        <p>Loading certificate data...</p>
      </div>

      <!-- Error message -->
      <div v-else-if="loadError" class="error-state">
        <p>Error: {{ loadError }}</p>
        <p>Please check the submission ID and try again.</p>
      </div>

      <!-- Certificate content -->
      <div
        v-else
        id="element-to-print"
        ref="elementRef"
        :style="{ transform: `scale(${desktopScale})` }"
      >
        <section
          class="u-align-center u-clearfix u-container-align-center-xl u-section-1"
          id="carousel_982e"
        >
          <div class="u-clearfix u-sheet u-valign-top-xl u-sheet-1">
            <div
              class="data-layout-selected u-clearfix u-expanded-width-md u-expanded-width-sm u-expanded-width-xs u-layout-wrap u-layout-wrap-1"
            >
              <div class="u-layout">
                <div class="u-layout-col">
                  <div
                    id="bgImg"
                    :class="[
                      'u-container-style',
                      'u-image',
                      'u-layout-cell',
                      'u-size-60',
                      'u-image-1',
                      backgroundClass,
                    ]"
                    data-image-width="1785"
                    data-image-height="2526"
                  >
                    <div class="u-container-layout u-container-layout-1">
                      <p class="u-align-center u-text u-text-1" id="company_name">
                        {{ certificateData.companyName }}
                      </p>
                      <p
                        class="u-align-left-xl u-align-right-lg u-align-right-md u-align-right-sm u-align-right-xs u-text u-text-2"
                        id="result"
                      >
                        {{ certificateData.valuationLower }} {{ certificateData.currency }} ~
                        {{ certificateData.valuationUpper }} {{ certificateData.currency }}
                      </p>
                      <p class="u-align-right u-text u-text-3" id="middle"></p>
                      <p class="u-align-right u-text u-text-4" id="date">
                        {{ certificateData.date }}
                      </p>
                      <p class="u-align-right u-text u-text-5" id="certificate_number">
                        {{ certificateData.certificateNumber }}
                      </p>
                      <span id="signature_valuator">{{ certificateData.consultantName }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import html2pdf from "html2pdf.js";
import Watermark from "@/components/Watermark.vue";
import { formatNumberWithCommas, getFormattedDate } from "@/utils/certificateUtils";
import { chatApi } from "@/api/api";

// Certificate data interface
interface CertificateData {
  companyName: string;
  valuationLower: string;
  valuationUpper: string;
  currency: string;
  date: string;
  certificateNumber: string;
  consultantName: string;
  companyType: number; // 1=general, 2=innovation, 8=patent, 9=bank
  showWatermark: boolean;
}

// API Response interfaces
interface ValuationResult {
  currency: string;
  lower_bound: number;
  upper_bound: number;
}

interface SubmissionItem {
  certificate_id: string;
  valuation_result: ValuationResult;
  valuation_log: {
    company_name: string;
  };
  create_time: string;
  report_type: string;
  config: {
    certification_id: string;
    case_name: string;
    consultant_name: string;
    [key: string]: any;
  };
}

interface SubmissionResponse {
  total: number;
  items: SubmissionItem[];
}

// Get route to access submission_id and valuation_subject from query parameters
const route = useRoute();
const submissionId = route.query.id as string;
const valuationSubject = route.query.subject as string;

// Loading state
const isLoading = ref(true);
const loadError = ref<string | null>(null);

// Placeholder data
const certificateData = ref<CertificateData>({
  companyName: "Acme Technology Inc.",
  valuationLower: formatNumberWithCommas(19571271),
  valuationUpper: formatNumberWithCommas(26478778),
  currency: "GBP",
  date: getFormattedDate(new Date()),
  certificateNumber: "GB15012212070000234",
  consultantName: "Peng Xiong",
  companyType: 1, // 1=general, 2=innovation, 8=patent, 9=bank
  showWatermark: false,
});

// Fetch certificate data from API
const fetchCertificateData = async () => {
  try {
    isLoading.value = true;
    loadError.value = null;

    // Validate submission ID exists
    if (!submissionId) {
      loadError.value =
        "Missing submission ID. Please provide an ID in the URL (?id=your-submission-id)";
      isLoading.value = false;
      return;
    }

    const response = (await chatApi.getSubmissionDetail(submissionId)) as SubmissionResponse;

    if (response.items && response.items.length > 0) {
      const submissionData = response.items[0]!;
      const valuationResult = submissionData.valuation_result;

      // Get certification ID - prefer from config, fallback to certificate_id
      const certificationId =
        submissionData.config?.certification_id || submissionData.certificate_id;

      // Get company name - prefer from config.case_name, fallback to valuation_log.company_name
      const companyName =
        submissionData.config?.case_name ||
        submissionData.valuation_log?.company_name ||
        "Company Name";

      // Get consultant name from config or use default
      const consultantName = submissionData.config?.consultant_name || "Peng Xiong";

      // Determine company type based on valuation_subject from query parameter
      // STARTUP -> 1 (general), PATENT -> 8 (patent)
      let companyType = 1; // default to general
      if (valuationSubject === "PATENT") {
        companyType = 8;
      } else if (valuationSubject === "STARTUP") {
        companyType = 1;
      }

      // Update certificate data with API response
      certificateData.value = {
        ...certificateData.value,
        currency: valuationResult.currency,
        valuationLower: formatNumberWithCommas(valuationResult.lower_bound),
        valuationUpper: formatNumberWithCommas(valuationResult.upper_bound),
        companyName: companyName,
        certificateNumber: certificationId,
        date: getFormattedDate(new Date(submissionData.create_time)),
        consultantName: consultantName,
        companyType: companyType,
      };
    } else {
      loadError.value = "No submission data found";
    }
  } catch (error) {
    console.error("Error fetching certificate data:", error);
    loadError.value = error instanceof Error ? error.message : "Failed to load certificate data";
  } finally {
    isLoading.value = false;
  }
};

// Computed property to determine background class based on company type
const backgroundClass = computed(() => {
  const type = certificateData.value.companyType;
  if (type === 1 || type === 2) {
    return "bgImg-ge";
  } else if (type === 8) {
    return "bgImg-patent";
  } else if (type === 9) {
    return "bgImg-bank";
  }
  return "bgImg-ge"; // default
});

// Track the rendered certificate element to scale it on desktop screens
const elementRef = ref<HTMLElement | null>(null);
const wrapperRef = ref<HTMLElement | null>(null);
const desktopScale = ref(1);

const updateScale = () => {
  const element = elementRef.value;
  const wrapper = wrapperRef.value;

  if (!element || !wrapper) return;

  // Skip scaling while generating PDF to avoid blurry exports
  if (element.classList.contains("pdf")) {
    desktopScale.value = 1;
    return;
  }

  // Mobile already fits naturally
  if (window.innerWidth < 1024) {
    desktopScale.value = 1;
    return;
  }

  const header = document.querySelector(".certificate-header") as HTMLElement | null;
  const headerHeight = header?.offsetHeight ?? 0;
  const availableHeight = window.innerHeight - headerHeight - 40; // leave more breathing room
  const availableWidth = wrapper.clientWidth - 40; // leave more breathing room on sides

  const heightScale = availableHeight / element.scrollHeight;
  const widthScale = availableWidth / element.scrollWidth;

  // Scale down a bit more (85%) to ensure better fit with more space around
  const nextScale = Math.min(heightScale, widthScale, 1) * 0.85;
  desktopScale.value = nextScale > 0 ? nextScale : 1;
};

onMounted(async () => {
  // Fetch certificate data first
  await fetchCertificateData();

  // Then update scale
  nextTick(updateScale);
  window.addEventListener("resize", updateScale, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", updateScale);
});

// Download PDF function
const downloadPDF = () => {
  const element = document.getElementById("element-to-print");
  if (!element) return;

  // Temporarily disable scaling for crisp PDF output
  const previousScale = desktopScale.value;
  desktopScale.value = 1;

  // Simply add pdf class - let CSS handle the rest
  element.classList.add("pdf");

  // Adjust date and certificate number position for PDF
  const dateElement = document.getElementById("date");
  const certNumberElement = document.getElementById("certificate_number");

  const originalDateBottom = dateElement?.style.bottom;
  const originalCertBottom = certNumberElement?.style.bottom;

  if (dateElement) {
    dateElement.style.bottom = "31.4rem";
  }
  if (certNumberElement) {
    certNumberElement.style.bottom = "25.4rem";
  }

  const options = {
    margin: [1.5, 1.5, 0.65, 0.2],
    filename: "certification.pdf",
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: {
      scale: 2,
      width: 1140,
      useCORS: true,
      logging: false,
      windowWidth: 1140,
    },
    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
  };

  html2pdf()
    .set(options)
    .from(element)
    .save()
    .then(() => {
      // Remove pdf class after generation
      element.classList.remove("pdf");
      desktopScale.value = previousScale;
      nextTick(updateScale);
      // Restore original styles
      if (dateElement) {
        dateElement.style.bottom = originalDateBottom || "";
      }
      if (certNumberElement) {
        certNumberElement.style.bottom = originalCertBottom || "";
      }
    })
    .catch((error: any) => {
      console.error("PDF generation error:", error);
      // Clean up even on error
      element.classList.remove("pdf");
      // Restore original styles
      if (dateElement) {
        dateElement.style.bottom = originalDateBottom || "";
      }
      if (certNumberElement) {
        certNumberElement.style.bottom = originalCertBottom || "";
      }
    });
};
</script>

<style scoped>
@import "./certificate.css";
@import "/css/nicepage.css";
</style>

<style>
/* Global styles for certificate page */
body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
}

.certificate-container {
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  box-sizing: border-box;
}

/* Simple header */
.certificate-header {
  position: relative;
  z-index: 100;
  padding: 12px 16px;
}

.header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.header-logo {
  display: inline-flex;
  align-items: center;
}

.logo-image {
  max-height: 56px;
  width: auto;
  display: block;
}

#downloadButton {
  background: #478ac9;
  color: white;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  white-space: nowrap;
}

#downloadButton:hover {
  background: #3a7ab8;
}

.certificate-content-wrapper {
  padding: 20px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  flex: 1;
  overflow: auto;
}

.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  text-align: center;
  color: #333;
}

.loading-state p {
  font-size: 16px;
  color: #478ac9;
}

.error-state p {
  font-size: 16px;
  color: #d32f2f;
  margin: 8px 0;
}

.error-state p:first-child {
  font-weight: 600;
}

#element-to-print {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  box-sizing: border-box;
  transform-origin: top center;
}

/* Ensure certificate section is fully visible and scrollable */
.u-section-1 {
  width: 100%;
  overflow: visible;
  display: block;
}

.u-section-1 .u-sheet-1 {
  width: 100%;
  overflow: visible;
  display: block;
}

.u-section-1 .u-layout-wrap-1 {
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
}

/* Make background image maintain proper aspect ratio */
.u-section-1 .u-image-1 {
  background-size: 100% auto;
  background-repeat: no-repeat;
}

/* Tighter header on phones */
@media (max-width: 767px) {
  .certificate-container .certificate-header {
    min-height: 0;
    padding: 8px 12px;
  }

  .certificate-container .certificate-header .header-bar {
    min-height: 0;
    gap: 10px;
  }

  .certificate-container .certificate-header .header-logo {
    margin: 0;
  }

  .certificate-container .certificate-header .logo-image {
    max-height: 30px;
    max-width: 110px;
    width: auto;
  }

  #downloadButton {
    padding: 6px 12px;
    font-size: 12px;
    height: auto;
    line-height: 1.2;
  }
}
</style>
